import { useSyncExternalStore } from 'react'
import { focusManager, onlineManager } from '@tanstack/react-query'
import { apiUrl } from './backend'
import { renewToken } from './session-token'
import { onDesktopVisibility, onDesktopWake } from './desktop-wake'

/**
 * Can the backend be reached right now? One answer for the whole app, and one ordered way back.
 *
 * WHY THIS EXISTS — after a long idle, or a laptop waking, the network is not back for a few
 * seconds (DNS, Wi-Fi, the VPN). Every background poll (presence every 5 s, artefacts and folders
 * every 4 s, cost every 5 s, plus the refetch of every other query on focus) failed in that window,
 * and each failure opened the global error dialog, which takes focus. The dialogs stacked up, one
 * per endpoint, over the editor. None of them was an error the member could act on. The network
 * was simply not back yet, and there was nothing to do but wait.
 *
 * So a TRANSIENT failure of a background read — a fetch rejection, a 502/503/504, or the
 * passerelle's `backend_unreachable` — is no longer shown as an error. It moves the app into
 * `reconnecting`:
 *
 *  1. TanStack Query is told the app is offline (`onlineManager.setOnline(false)`). Every poll and
 *     refetch pauses, because queries use the default `networkMode: 'online'`.
 *  2. `GET /v1/health` is probed with a backoff from 1 s to 30 s, using a raw fetch rather than
 *     request(), so a failed probe feeds nothing back into this store.
 *  3. Once the backend answers, recovery runs IN ORDER: renew the token, recover the rooms, then
 *     let TanStack refetch. The order matters. On a wake, all of these used to start at the same
 *     moment, before DNS or the VPN was up. Renewal failed and fell back to the expired token, and
 *     the rooms reconnected with it and were refused.
 *
 * The same ordered recovery runs on the signals that mean "the member, or the network, is back":
 * the browser's `online`, the page becoming visible, and Niobe Desktop's wake and window-shown
 * messages. Those start in `checking`: queries are paused and the backend is probed at once. The
 * "Reconnecting…" banner shows only if that first probe fails, so a routine tab switch never
 * flashes it.
 *
 * WHAT STILL SHOWS THE DIALOG — member actions (any non-GET: save, rename, delete, export) and
 * non-transient errors. A member who clicked something must hear that it failed. See api.ts.
 */

export type Connectivity =
  /** Nothing to report. */
  | 'online'
  /** A recovery signal arrived; queries are paused while the backend is probed. No banner. */
  | 'checking'
  /** The backend could not be reached. Queries are paused and the banner is up until it answers. */
  | 'reconnecting'

/** First wait between probes, doubled after each miss up to PROBE_MAX_MS. */
export const PROBE_BASE_MS = 1000
export const PROBE_MAX_MS = 30_000
/** One probe's ceiling. A captive portal or a DNS lookup that never answers must not stall recovery. */
export const PROBE_TIMEOUT_MS = 5000

/** The passerelle's code for "the backend cannot be reached" (client/passerelle/backend-proxy.ts). */
export const BACKEND_UNREACHABLE = 'backend_unreachable'

/**
 * A response that says "the backend is unreachable", rather than "the backend refused this".
 *
 * A 502/503/504 counts only WITHOUT the backend's `{ error }` envelope, which means a gateway in
 * front of the backend wrote it (the Azure ingress while the app restarts, a proxy). A backend that
 * answers is reachable, whatever the status. For example, its `502 sharepoint_unreachable` means
 * Graph failed, and the pane that asked says so itself.
 *
 * @param code      the body's `error.code`, '' when it had none
 * @param enveloped the body carried an `error` field at all (object or string)
 */
export function isTransientStatus(status: number, code = '', enveloped = code !== ''): boolean {
  if (code === BACKEND_UNREACHABLE) return true
  return (status === 502 || status === 503 || status === 504) && !enveloped
}

// ─── the store ────────────────────────────────────────────────────────────────────────────────

let state: Connectivity = 'online'
const listeners = new Set<() => void>()

function setState(next: Connectivity): void {
  if (state === next) return
  state = next
  for (const l of [...listeners]) l()
}

export function getConnectivity(): Connectivity {
  return state
}

export function subscribeConnectivity(cb: () => void): () => void {
  listeners.add(cb)
  return () => {
    listeners.delete(cb)
  }
}

export function useConnectivity(): Connectivity {
  return useSyncExternalStore(subscribeConnectivity, getConnectivity, getConnectivity)
}

/**
 * Step 4 of the recovery: "the backend answers and the token is fresh, repair your room now".
 * useRoomHealth subscribes, once per open room.
 */
const roomListeners = new Set<() => void>()

export function onRoomsRecover(cb: () => void): () => void {
  roomListeners.add(cb)
  return () => {
    roomListeners.delete(cb)
  }
}

// ─── the probe ────────────────────────────────────────────────────────────────────────────────

/**
 * Does the backend answer? A raw fetch, not request(): a failed probe must not report itself back
 * into this store, and it carries no token, so it cannot 401 its way into the auth flow.
 *
 * 401 counts as an answer. A backend deployed before `/v1/health` existed answers the route with
 * its auth hook, and that still proves it is reachable.
 *
 * The timeout ABORTS the request rather than just stopping waiting for it: a black-holed connection
 * left hanging on every backoff step would hold the browser's per-host connection slots.
 */
export async function probeBackend(): Promise<boolean> {
  try {
    const signal = typeof AbortSignal.timeout === 'function' ? AbortSignal.timeout(PROBE_TIMEOUT_MS) : undefined
    const res = await fetch(apiUrl('/v1/health'), { cache: 'no-store', ...(signal ? { signal } : {}) })
    return res.ok || res.status === 401
  } catch {
    return false
  }
}

// ─── the recovery loop ────────────────────────────────────────────────────────────────────────

/** The recovery in progress, if any. Every trigger joins it rather than starting another. */
let run: Promise<void> | null = null
/** True once the running recovery has passed its probe. A FAILURE reported after that point may
 *  be a fresh outage, so it gets a recovery of its own (`again`). A TRIGGER arriving then has
 *  nothing left to add: the rooms and the refetch are already on their way. */
let resuming = false
let again = false
/** Ends the current backoff wait early: a wake or `online` means "probe now". */
let cutWait: (() => void) | null = null

function wait(ms: number): Promise<void> {
  return new Promise((resolve) => {
    const done = (): void => {
      clearTimeout(timer)
      if (cutWait === done) cutWait = null
      resolve()
    }
    const timer = setTimeout(done, ms)
    cutWait = done
  })
}

function pause(next: Connectivity): void {
  // Never downgrade the banner to the quieter state: 'checking' only ever replaces 'online'.
  if (next === 'reconnecting' || state === 'online') setState(next)
  onlineManager.setOnline(false)
}

/**
 * Probe until the backend answers, then resume in order. A 'failure' waits PROBE_BASE_MS before its
 * first probe; a 'trigger' probes at once.
 */
function recover(kind: 'failure' | 'trigger'): Promise<void> {
  if (run) {
    if (kind === 'failure' && resuming) again = true
    if (kind === 'trigger' && !resuming) cutWait?.()
    return run
  }
  run = (async () => {
    try {
      let delay = PROBE_BASE_MS
      if (kind === 'failure') await wait(delay)
      while (!(await probeBackend())) {
        setState('reconnecting')
        await wait(delay)
        delay = Math.min(delay * 2, PROBE_MAX_MS)
      }
      resuming = true
      // Renewal failures are handled where the token is used (session-token.ts): a refused room
      // retries on the next successful renewal, and REST retries a 401 once.
      await renewToken().catch(() => undefined)
      for (const cb of [...roomListeners]) {
        try {
          cb()
        } catch {
          /* one room must not stop the others */
        }
      }
    } finally {
      // Cleared BEFORE going back online, in the same tick: a trigger that lands after this point
      // must start a recovery of its own, not join one that is already over.
      run = null
      resuming = false
    }
    if (again) {
      again = false
      pause('reconnecting')
      void recover('failure')
      return
    }
    setState('online')
    // Last, so the refetch goes out with the fresh token. TanStack resumes paused fetches and
    // refetches stale queries on reconnect.
    onlineManager.setOnline(true)
  })()
  return run
}

/**
 * A background read failed on the network (called by api.ts#request). Shows the banner, pauses
 * every poll, and probes until the backend answers. The first probe waits PROBE_BASE_MS, so a
 * single blip costs one second of banner, not a dialog.
 */
export function reportTransientFailure(): void {
  pause('reconnecting')
  void recover('failure')
}

/**
 * The member or the network may be back (a wake, `online`, the page visible again): run the
 * ordered recovery now. Resolves when it completes. Exported for tests and for code that has its
 * own reason to believe the network changed.
 */
export function recoverNow(): Promise<void> {
  pause('checking')
  return recover('trigger')
}

// ─── wiring ───────────────────────────────────────────────────────────────────────────────────

let uninstall: (() => void) | null = null

/**
 * Wire the store to the signals that start a recovery. Call once at startup (main.tsx).
 *
 * TanStack's own online listener is replaced by a no-op. Left in place, it would flip queries back
 * online on the browser's `online` event, before anything proved the backend reachable. This store
 * owns that flag now.
 */
export function installConnectivity(): () => void {
  if (uninstall) return uninstall
  onlineManager.setEventListener(() => () => undefined)

  const onOnline = (): void => void recoverNow()
  // DevTools "Offline", or the OS dropping the network: no need to wait for a request to fail.
  const onOffline = (): void => reportTransientFailure()
  const onVisibility = (): void => {
    if (document.visibilityState === 'visible') void recoverNow()
  }
  window.addEventListener('online', onOnline)
  window.addEventListener('offline', onOffline)
  document.addEventListener('visibilitychange', onVisibility)
  // Desktop only; no-ops in the browser. A resumed machine fires neither `online` nor
  // `visibilitychange` when the window was already visible, so the shell says so.
  const stopWake = onDesktopWake(() => void recoverNow())
  // The desktop window reads itself as visible even in the tray (see onDesktopVisibility), so the
  // shell's report drives TanStack's focus instead: polls skip their ticks while it is hidden, and
  // refetch when it is shown again.
  const stopVisibility = onDesktopVisibility((visibility) => {
    if (visibility === 'hidden') {
      focusManager.setFocused(false)
      return
    }
    // Recover first, so the refetch that focus triggers waits for the probe instead of racing it.
    void recoverNow()
    focusManager.setFocused(true)
  })

  uninstall = () => {
    window.removeEventListener('online', onOnline)
    window.removeEventListener('offline', onOffline)
    document.removeEventListener('visibilitychange', onVisibility)
    stopWake()
    stopVisibility()
    uninstall = null
  }
  return uninstall
}
